import { useMemo, useState } from 'react';
import { StatCard, Card, Skeleton, EmptyState } from '@vitness/ui';
import { DonutChart, PALETTE } from '@vitness/charts';
import { AlertCircle } from '@vitness/icons';
import { useStore } from '../lib/store';
import { fmtMoney, fmtPct, totalNetWorth, effectiveTaxPct, emergencyFundPct } from '../lib/format';
import { cashFlowSplit, emergencyFundTiers, monthsCovered } from '@shared/finance';
import { BUCKET_TARGETS } from '@shared/groups';
import type { CspBucket } from '@shared/types';
import { AllocationBar } from '../features/dashboard/AllocationBar';
import { CashFlow } from '../features/dashboard/CashFlow';
import { Hero } from '../features/dashboard/Hero';
import { SegTabs } from '../components/SegTabs';
import { RouteHeader } from '../components/RouteHeader';
import { compactMoneyFmt } from '../lib/format';
import { SectionLabel, Note, Num, Dot, autoGrid, pairGrid } from '../components/ui';

const EF_TARGETS = [3, 6, 9, 12];

export function DashboardRoute() {
  const { loading, error, data, cats } = useStore();
  const [spendTab, setSpendTab] = useState<'flow' | 'split'>('flow');

  const model = useMemo(() => {
    if (!data || !cats.length) return null;
    const net = data.income.netMonthly;

    // One shared split, so the stat cards and the Sankey can't disagree about
    // what take-home is committed to.
    const split = cashFlowSplit(cats, net, data.income.grossMonthly);
    const { byBucket, committed } = split;
    const latest = data.netWorth[data.netWorth.length - 1];
    const previous = data.netWorth[data.netWorth.length - 2];

    return {
      net,
      gross: data.income.grossMonthly,
      committed,
      surplus: net - committed,
      preTax: split.preTax,
      postTax: split.postTax,
      byBucket,
      efTiers: emergencyFundTiers(byBucket),
      latest,
      nwNow: latest ? totalNetWorth(latest) : 0,
      nwDelta: latest && previous ? totalNetWorth(latest) - totalNetWorth(previous) : 0,
      previousName: previous?.name ?? '',
      taxPct: effectiveTaxPct(net, data.income.grossMonthly),
      activeCount: data.expenses.filter(e => e.status === 'A').length,
      totalCount: data.expenses.length,
      // Spend grouped by the *Notion* category, emoji intact — closer to how
      // you actually think about it than the 13 rollup groups.
      ...(() => {
        const by = new Map<string, number>();
        for (const c of cats) {
          for (const i of c.items) {
            // Chart labels are plain text, so only an emoji icon can ride along
            // — an image icon comes back as a URL and would read as noise.
            const emoji = i.categoryIcon && !/^https?:\/\//.test(i.categoryIcon) ? i.categoryIcon : '';
            const key = `${emoji} ${i.categoryName ?? c.name}`.trim();
            by.set(key, (by.get(key) ?? 0) + i.base);
          }
        }
        const spend = [...by.entries()].filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
        return { spend, spendTotal: spend.reduce((s, [, v]) => s + v, 0) };
      })(),
    };
  }, [data, cats]);

  if (loading) return <DashboardSkeleton />;

  if (error) {
    return (
      <>
        <RouteHeader title="Dashboard" />
        <EmptyState
          icon={<AlertCircle size={20} />}
          title="Couldn't load your data"
          description={error}
        />
      </>
    );
  }

  if (!model || !model.latest) {
    return (
      <>
        <RouteHeader title="Dashboard" />
        <EmptyState
          icon={<AlertCircle size={20} />}
          title="No data yet"
          description="Run a Notion sync to pull your expenses and net-worth history."
        />
      </>
    );
  }

  return (
    <>
      <RouteHeader
        title="Dashboard"
        stats={[
          { label: 'Net worth', value: fmtMoney(model.nwNow) },
          { label: 'Unallocated', value: `${fmtMoney(model.surplus)}/mo`,
            tone: model.surplus >= 0 ? 'good' : 'bad' },
          { label: 'EF runway', value: `${monthsCovered(model.latest.emergencyFund, model.efTiers[0].burn).toFixed(1)} mo` },
          { label: 'Active', value: `${model.activeCount} of ${model.totalCount}`, tone: 'muted' },
        ]}
      />

      {/* the answer first — everything below is supporting detail */}
      <Hero
        surplus={model.surplus}
        bufferMonths={monthsCovered(model.latest.emergencyFund, model.efTiers[0].burn)}
        syncAgeMinutes={data?.sync.ageMinutes ?? null}
      />

      {/* headline numbers — 2×2 so the pairs stay paired at every width */}
      <div style={pairGrid}>
        <StatCard label="Net monthly" value={fmtMoney(model.net)} trend={`${fmtMoney(model.net * 12)} / yr`} />
        <StatCard label="Committed" value={fmtMoney(model.committed)} trend={`${fmtPct((model.committed / model.net) * 100)} of net`} trendUp={false} />
        <StatCard
          label="Unallocated"
          value={fmtMoney(model.surplus)}
          trend={model.surplus >= 0 ? 'in the black' : 'over budget'}
          trendUp={model.surplus >= 0}
        />
        <StatCard label="Effective tax" value={fmtPct(model.taxPct)} trend={`gross ${fmtMoney(model.gross)}`} />
      </div>

      {/* CSP allocation */}
      <Card variant="outlined" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <SectionLabel style={{ marginBottom: 0 }}>CSP allocation · target vs actual</SectionLabel>
          {/*
            Spelled out because Projections has a similar-looking meter and
            they will never agree: this card is the budget as it exists in
            Notion, full stop. Scenario dials, caps and events live in
            Projections and are simulations layered on top — nothing done
            there changes these numbers.
          */}
          <Num style={{ fontSize: 10, color: 'var(--vn-text-muted)' }}>
            as synced from Notion — scenario dials never touch this
          </Num>
        </div>
        <div style={{ marginTop: 6 }} />
        {(Object.keys(BUCKET_TARGETS) as CspBucket[]).map(b => (
          <AllocationBar
            key={b}
            label={BUCKET_TARGETS[b].name}
            bucket={b}
            value={model.byBucket[b]}
            pct={(model.byBucket[b] / model.net) * 100}
            low={BUCKET_TARGETS[b].low}
            high={BUCKET_TARGETS[b].high}
          />
        ))}
        <Note>
          Measured against {fmtMoney(model.net)}/mo take-home. The dark tick on each bar marks the
          target midpoint; the lighter band behind it is the acceptable range.
        </Note>
      </Card>

      {/* net worth + emergency fund */}
      <div style={{ ...autoGrid(280), marginTop: 16 }}>
        <Card variant="outlined">
          <SectionLabel>Net worth</SectionLabel>
          <div style={{ fontFamily: 'var(--vn-font-display)', fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em' }}>
            {fmtMoney(model.nwNow)}
          </div>
          <div className="vd-num" style={{ fontSize: 11, color: model.nwDelta >= 0 ? 'var(--vn-success)' : 'var(--vn-danger)', marginTop: 4 }}>
            {model.nwDelta >= 0 ? '▲' : '▼'} {fmtMoney(Math.abs(model.nwDelta))} since {model.previousName}
          </div>
          <div style={{ marginTop: 14, display: 'grid', gap: 6 }}>
            <Row label="Assets" value={model.latest.assets} />
            <Row label="Investments" value={model.latest.investments} />
            <Row label="Savings" value={model.latest.savings} />
            <Row label="Emergency fund" value={model.latest.emergencyFund} />
            <Row label="Debt" value={-model.latest.debt} negative />
          </div>
        </Card>

        <Card variant="outlined">
          <SectionLabel>Emergency fund runway</SectionLabel>
          <div style={{ fontFamily: 'var(--vn-font-display)', fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em' }}>
            {fmtMoney(model.latest.emergencyFund)}
          </div>
          <div className="vd-num" style={{ fontSize: 11, color: 'var(--vn-text-muted)', marginTop: 4 }}>
            {model.efTiers.map(t => `${monthsCovered(model.latest.emergencyFund, t.burn).toFixed(1)}mo ${t.label}`).join(' · ')}
          </div>

          {/*
            A period × tier matrix rather than one bar per period. "Am I covered
            for six months?" has three different answers depending on whether the
            six months includes investing and how much guilt-free spending
            survives the emergency — and only showing the cheapest of the three
            made the fund look healthier than it is.
          */}
          <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
            <div className="vd-efgrid vd-efgrid--head">
              <span />
              {model.efTiers.map(t => (
                <span key={t.key} title={`${t.detail} — ${fmtMoney(t.burn)}/mo`}
                  style={{
                    fontFamily: 'var(--vn-font-mono)', fontSize: 8.5, textTransform: 'uppercase',
                    letterSpacing: '0.06em', color: 'var(--vn-text-muted)', textAlign: 'right',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                  {t.label}
                </span>
              ))}
            </div>

            {EF_TARGETS.map(months => (
              <div key={months} className="vd-efgrid">
                <span className="vd-efgrid__row-label">{months} mo</span>
                {model.efTiers.map(t => {
                  const target = t.burn * months;
                  const pct = emergencyFundPct(model.latest.emergencyFund, t.burn, months);
                  return (
                    <div key={t.key} className="vd-efgrid__cell" title={`${t.detail} — ${fmtMoney(t.burn)}/mo`}>
                      {/* The tier name, carried per-cell for the phone layout
                          where the column header is a row above that no longer
                          lines up. Hidden at every other width. */}
                      <span className="vd-efgrid__cell-tier">{t.label}</span>
                      <div className="vd-num vd-efgrid__read">
                        <span style={{ color: 'var(--vn-text-secondary)' }}>{fmtMoney(target)}</span>
                        <span style={{ color: 'var(--vn-text-muted)' }}>{fmtPct(pct, 0)}</span>
                      </div>
                      <div style={{
                        height: 6, borderRadius: 3, background: 'var(--vn-surface-inset)',
                        border: '1px solid var(--vn-border-default)', overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${pct}%`, height: '100%',
                          background: pct >= 100 ? 'var(--vn-success)' : pct >= 50 ? 'var(--vn-warning)' : 'var(--vn-danger)',
                          transition: 'width .4s ease',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <Note>
            Targets match Notion's <strong>Target Emergency Fund</strong> table.
            <strong> FEXSGFS</strong> is fixed costs plus 50% of guilt-free spending —
            in an emergency you cut discretionary spending hard, not to zero. The
            next tier adds investment contributions, the last swaps the half-share
            for the whole guilt-free budget. Hover a column for its monthly burn.
          </Note>
        </Card>
      </div>

      {/* spending — two ways of looking at the same money */}
      <Card variant="outlined" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <SectionLabel style={{ marginBottom: 0 }}>
            {spendTab === 'flow' ? 'Cash flow · gross to categories' : 'Spending · by category'}
          </SectionLabel>
          <SegTabs
            label="Spending view" value={spendTab} onChange={setSpendTab}
            options={[
              { value: 'flow', label: 'Cash flow' },
              { value: 'split', label: 'Spending' },
            ]}
          />
        </div>

        {spendTab === 'flow' && (
          <CashFlow cats={cats} net={model.net} gross={model.gross} />
        )}

        {spendTab === 'split' && (
        <>
        {/*
          On a wide screen the ring sits beside a real legend: bigger donut, and
          a list that can carry the actual amounts and share rather than the
          bare swatch-and-name Chart.js draws inside the canvas. Below 860px it
          collapses back to the built-in legend under the ring, which is what
          works on a phone.
        */}
        <div className="vd-donut">
          <div className="vd-donut__chart">
            <DonutChart
              labels={model.spend.map(([k]) => k)}
              data={model.spend.map(([, v]) => v)}
              height={320}
              legend={false}
              formatValue={v => `${compactMoneyFmt(v)} / mo`}
              colors={model.spend.map((_, i) => PALETTE[i % PALETTE.length])}
            />
            <div className="vd-donut__centre">
              <Num style={{ fontSize: 10, color: 'var(--vn-text-muted)' }}>total</Num>
              <div style={{ fontFamily: 'var(--vn-font-display)', fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>
                {fmtMoney(model.spendTotal)}
              </div>
              <Num style={{ fontSize: 10, color: 'var(--vn-text-muted)' }}>per month</Num>
            </div>
          </div>

          <ul className="vd-donut__legend">
            {model.spend.map(([name, value], i) => (
              <li key={name}>
                <Dot color={PALETTE[i % PALETTE.length]} size={7} />
                <span className="vd-donut__name" title={name}>{name}</span>
                <Num className="vd-donut__value">{fmtMoney(value)}</Num>
                <Num className="vd-donut__share">
                  {model.spendTotal ? Math.round((value / model.spendTotal) * 100) : 0}%
                </Num>
              </li>
            ))}
          </ul>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--vn-border-default)',
        }}>
          <Num style={{ fontSize: 11, color: 'var(--vn-text-muted)' }}>
            {model.spend.length} categories
          </Num>
          <Num style={{ fontSize: 13, fontWeight: 600 }}>
            {fmtMoney(model.spendTotal, 2)}
            <span style={{ fontSize: 10, color: 'var(--vn-text-muted)', fontWeight: 400 }}> / month</span>
          </Num>
        </div>
        </>
        )}
      </Card>

    </>
  );
}

// ─────────────────────────── bits ───────────────────────────





function Row({ label, value, negative }: { label: string; value: number; negative?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--vn-font-mono)', fontSize: 11.5 }}>
      <span style={{ color: 'var(--vn-text-muted)' }}>{label}</span>
      <span style={{ color: negative ? 'var(--vn-danger)' : 'var(--vn-text-primary)' }}>
        {fmtMoney(value)}
      </span>
    </div>
  );
}

/** Mirrors the loaded layout block for block, so content replaces bones
    without anything jumping. */
function DashboardSkeleton() {
  return (
    <>
      <Skeleton style={{ height: 62, marginBottom: 20 }} />
      <Skeleton style={{ height: 118, marginBottom: 14 }} />   {/* hero */}
      <div style={pairGrid}>
        {[0, 1, 2, 3].map(i => <Skeleton key={i} style={{ height: 84 }} />)}
      </div>
      <Skeleton style={{ height: 220, marginTop: 16 }} />
      <div style={{ ...autoGrid(280), marginTop: 16 }}>
        <Skeleton style={{ height: 240 }} />
        <Skeleton style={{ height: 240 }} />
      </div>
    </>
  );
}
